import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import {
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { createContext, forwardRef, useCallback, useContext, useId, useMemo } from 'react';
import { backgroundColorDarkTheme } from '../../styles/global.styles';

const icons = Object.freeze({
  unchecked: <CheckBoxOutlineBlankIcon fontSize="small" />,
  checked: <CheckBoxIcon fontSize="small" />,
  indeterminate: <IndeterminateCheckBoxIcon fontSize="small" />,
});

interface DropdownContextType {
  showSelectAll: boolean;
  optionsCount: number;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  handleToggleSelectAll: () => void;
}

const DropdownContext = createContext<DropdownContextType | null>(null);

const CustomPaper = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
  const { children, ...rest } = props;
  const context = useContext(DropdownContext);
  
  if (!context) {
    console.error('CustomPaper deve estar dentro de DropdownContext.Provider');
    return <Paper {...rest} ref={ref}>{children}</Paper>;
  }
  
  const { showSelectAll, optionsCount, isAllSelected, isIndeterminate, handleToggleSelectAll } = context;

  return (
    <Paper 
      {...rest} 
      ref={ref}
      sx={{ 
        backgroundColor: backgroundColorDarkTheme, 
        border: '1px solid gray', 
        color: '#ffffff' 
      }}
    >
      {showSelectAll && optionsCount > 0 && (
        <>
          <Box
            onMouseDown={(e) => e.preventDefault()} 
            onClick={handleToggleSelectAll}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
            }}
          >
            <Checkbox
              icon={icons.unchecked}
              checkedIcon={icons.checked}
              indeterminateIcon={icons.indeterminate}
              indeterminate={isIndeterminate} 
              checked={isAllSelected}
              style={{ marginRight: 8 }}
              sx={{ 
                color: 'gray',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&.Mui-checked, &.MuiCheckbox-indeterminate': { 
                  color: 'primary.main',
                  transform: 'scale(1.05)',
                } 
              }}
            />
            <Typography fontWeight={isAllSelected ? 600 : 400}>
              {isAllSelected ? 'Limpar Seleção' : 'Selecionar Todos'}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: 'gray' }} />
        </>
      )}
      {children}
    </Paper>
  );
});

CustomPaper.displayName = 'CustomPaper';

interface DropdownMultipleProps<T extends object> {
  label: string;
  options: T[];
  value: (string | number)[];
  onChange: (newValue: (string | number)[]) => void;
  labelField: keyof T;
  valueField: keyof T;
  isLoading?: boolean;
  errorMessage?: string;
  placeholder?: string;
  disabled?: boolean;
  showSelectAll?: boolean;
  optionsLimit?: number;
}

const DropdownMultipleComponent = <T extends object>({
  label,
  options = [],
  value = [],
  onChange,
  labelField,
  valueField,
  isLoading = false,
  errorMessage,
  placeholder,
  disabled = false,
  showSelectAll = true,
  optionsLimit,
}: DropdownMultipleProps<T>) => {
  const id = useId();

  const selectedOptions = useMemo(() => {
    const valueSet = new Set(value);
    return options.filter((option) => valueSet.has(option[valueField] as string | number));
  }, [options, value, valueField]);

  const filterOptions = useMemo(
    () => createFilterOptions<T>({ limit: optionsLimit }),
    [optionsLimit]
  );

  const isAllSelected = options.length > 0 && value.length === options.length;
  const isIndeterminate = value.length > 0 && value.length < options.length;

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: T[]) => {
      const newValuesArray = newValue.map((item) => item[valueField] as string | number);
      onChange(newValuesArray);
    },
    [onChange, valueField]
  );

  const handleToggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      onChange([]);
    } else {
      const allValues = options.map((opt) => opt[valueField] as string | number);
      onChange(allValues);
    }
  }, [isAllSelected, options, onChange, valueField]);

  const getOptionLabel = useCallback((option: T): string => {
    const val = option[labelField];
    return val === null || val === undefined ? '' : String(val);
  }, [labelField]);

  const contextValue = useMemo(() => ({
    showSelectAll,
    optionsCount: options.length,
    isAllSelected,
    isIndeterminate,
    handleToggleSelectAll
  }), [showSelectAll, options.length, isAllSelected, isIndeterminate, handleToggleSelectAll]);

  return (
    <FormControl fullWidth error={!!errorMessage} sx={{ mb: 3 }}>
      <DropdownContext.Provider value={contextValue}>
        <Autocomplete
          multiple
          limitTags={2}
          filterOptions={filterOptions}
          disableCloseOnSelect
          loading={isLoading}
          disabled={disabled}
          id={`${id}-autocomplete`}
          options={options}
          value={selectedOptions}
          onChange={handleChange}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, val) => option[valueField] === val[valueField]}
          noOptionsText={isLoading ? 'Carregando...' : 'Nenhuma opção disponível'}
          slots={{ paper: CustomPaper }}
          renderOption={(props, option, { selected }) => (
            <Box
              component="li"
              {...props}
              key={String(option[valueField])}
              sx={{
                backgroundColor: backgroundColorDarkTheme,
                color: '#ffffff',
                transition: 'background-color 0.2s ease !important',
                '&:hover:not([aria-selected="true"])': {
                  backgroundColor: '#2a2a2a !important',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: '#3a3a3a !important',
                },
                '&[aria-selected="true"]:hover': {
                  backgroundColor: '#4a4a4a !important',
                },
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Checkbox
                icon={icons.unchecked}
                checkedIcon={icons.checked}
                style={{ marginRight: 8 }}
                checked={selected}
                sx={{
                  color: 'gray',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.Mui-checked': { 
                    color: 'primary.main',
                    transform: 'scale(1.1)',
                  },
                  '& svg': {
                    transition: 'fill 0.2s ease-in-out, transform 0.2s ease-in-out',
                  }
                }}
              />
              <Typography noWrap flex={1}>
                {getOptionLabel(option)}
              </Typography>
            </Box>
          )}
          
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={selectedOptions.length === 0 ? placeholder : ''}
              slotProps={{
                inputLabel: {
                  sx: { color: '#ffffff', '&.Mui-focused': { color: '#ffffff' } },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#ffffff',
                  borderColor: 'gray',
                  flexWrap: 'nowrap', 
                  overflow: 'hidden',
                  '& fieldset': { borderColor: 'gray' },
                  '&:hover fieldset': { borderColor: '#ffffff' },
                  '&.Mui-focused': {
                    flexWrap: 'wrap',
                  },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  '& .MuiAutocomplete-tag': {
                    maxWidth: '100px',
                    transition: 'max-width 0.3s ease-in-out',
                  },
                  '&.Mui-focused .MuiAutocomplete-tag': {
                    maxWidth: '100%',
                  },
                  '& .MuiAutocomplete-tag .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                },
              }}
            />
          )}
          
          slotProps={{
            clearIndicator: {
              sx: {
                color: '#ffffff !important',
                '&:hover': {
                  color: '#ffffff !important',
                }
              }
            },
            popupIndicator: {
              sx: {
                color: '#ffffff !important',
                '&:hover': {
                  color: '#ffffff !important',
                }
              }
            },
            listbox: { sx: { maxHeight: '200px' } },
          }}
        />
      </DropdownContext.Provider>
      {!!errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default DropdownMultipleComponent;